import { useStore } from '@/hooks/useStore';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    Title,
    Tooltip,
    Legend,
    BarElement,
    ChartOptions
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useLocation } from 'react-router-dom';
import { isPathEk } from '@/utils/routes';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    Title,
    Tooltip,
    Legend,
    BarElement
);

const LABEL_ROLES = {
    HQ: 'HQ',
    FCPRM: 'FC - PRM',
    FCSTD: 'FC - STD',
    SO: 'SO',
    HO: 'HO'
};

export default observer(function ChartInstalledWithin24Hours() {
    // hook
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    // store
    const {
        dashboardStore: { statistics, getStatistics }
    } = useStore();

    // lifecycle
    useEffect(() => {
        getStatistics();
    }, []);

    const options: ChartOptions<'bar'> = {
        responsive: true,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true
            },
            title: {
                display: false
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0
                },
                suggestedMax: Math.max(
                    10,
                    Math.max(
                        ...(isEk ?
                            [statistics?.HQ ?? 0, statistics?.HO ?? 0] :
                            [
                                statistics?.HQ ?? 0,
                                statistics?.FCPRM ?? 0,
                                statistics?.FCSTD ?? 0,
                                statistics?.SO ?? 0
                            ])
                    )
                )
            }
        }
        // maintainAspectRatio: false
    };

    const labels = isEk ?
        [LABEL_ROLES.HQ, LABEL_ROLES.HO] :
        [LABEL_ROLES.HQ, LABEL_ROLES.FCPRM, LABEL_ROLES.FCSTD, LABEL_ROLES.SO];

    const data = useMemo(() => {
        return {
            labels,
            datasets: [
                {
                    label: '',
                    data: isEk ?
                        [statistics?.HQ ?? 0, statistics?.HO ?? 0] :
                        [
                            statistics?.HQ ?? 0,
                            statistics?.FCPRM ?? 0,
                            statistics?.FCSTD ?? 0,
                            statistics?.SO ?? 0
                        ],
                    borderWidth: 1.5,
                    borderColor: '#df0002',
                    borderRadius: 10,
                    backgroundColor: 'rgba(255, 99, 132, 0.5)',
                    maxBarThickness: 100
                }
            ]
        };
    }, [statistics]);

    return (
        <div className='max-h-[400px]'>
            <Bar options={options} data={data} />
        </div>
    );
});
